import { useCallback } from 'react';

import {
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  ENTIRE_ONLY_FIELDS,
} from '@/entities/chart/model/constants';
import useChartStateAction from '@/features/(charts)/chart-controller/hooks/use-chart-state-action/useChartStateAction';

type UseDimensionHandlersParams = {
  chartInfo: ChartContextProps;
  chartSeriesIndex?: number;
};

export const useDimensionHandlers = ({
  chartInfo,
  chartSeriesIndex,
}: UseDimensionHandlersParams) => {
  const isColumn = typeof chartSeriesIndex === 'number';
  const { state, setState, setSelectedField } = chartInfo;
  const { onFieldChange, onFieldItemChange } = useChartStateAction(chartInfo);

  const targetField: ChartFieldDef = isColumn
    ? state.series[chartSeriesIndex]?.cross_field || {
        type: CHART_FIELD.FEEDBACK_ALL,
        entire: true,
        candidates: [],
      }
    : state.field;

  const handleFieldChange = useCallback(
    (value: CHART_FIELD) => {
      onFieldChange(value, chartSeriesIndex);
    },
    [onFieldChange, chartSeriesIndex],
  );

  const handleFieldItemChange = useCallback(
    (values: StringNumberValue[]) => {
      onFieldItemChange(values, chartSeriesIndex);
    },
    [onFieldItemChange, chartSeriesIndex],
  );

  const handleTopLabelClick = useCallback(() => {
    setState((prev) => {
      if (isColumn) {
        return {
          ...prev,
          series: prev.series.map((series: ChartSeriesDef, index) =>
            index === chartSeriesIndex
              ? {
                  ...series,
                  cross_field: {
                    ...series.cross_field!,
                    entire: true,
                    candidates: [],
                  },
                }
              : (series),
          ),
        };
      }
      return {
        ...prev,
        field: {
          ...prev.field,
          entire: false,
          candidates: [],
        },
        selected_field: {
          method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
          items: [],
        },
      };
    });
    setSelectedField({
      items: [],
      method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
    });
  }, [setState, setSelectedField, isColumn, chartSeriesIndex]);

  const handleHeaderTopLabelClick = useCallback(
    (onDeselectAll: () => void, close: () => void) => {
      onDeselectAll();
      setState((prev) => ({
        ...prev,
        field: {
          ...prev.field,
          entire: true,
          candidates: [],
        },
        selected_field: {
          method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
        },
      }));
      setSelectedField({
        method: CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5,
      });
      close();
    },
    [setState, setSelectedField],
  );

  const handleClear = useCallback(() => {
    setState((prev) => {
      if (isColumn) {
        return {
          ...prev,
          series: prev.series.map((series: ChartSeriesDef, index) =>
            index === chartSeriesIndex
              ? {
                  ...series,
                  cross_field: {
                    ...series.cross_field!,
                    entire: ENTIRE_ONLY_FIELDS.has(series.cross_field!.type),
                    candidates: [],
                  },
                }
              : (series),
          ),
        };
      }
      return {
        ...state,
        field: {
          ...state.field,
          entire: false,
          candidates: [],
        },
      };
    });
  }, [setState, state, isColumn, chartSeriesIndex]);

  return {
    isColumn,
    targetField,
    state,
    handleFieldChange,
    handleFieldItemChange,
    handleTopLabelClick,
    handleHeaderTopLabelClick,
    handleClear,
  };
};
